import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Trophy, Loader2, Play, Sparkles, Crown, Share2, Link as LinkIcon, TrendingUp, Search, X } from "lucide-react";
import api from "@/lib/api";

const scoreColor = (n) => (n >= 80 ? "#10b981" : n >= 60 ? "#38bdf8" : n >= 40 ? "#f59e0b" : "#f43f5e");
const MEDAL = { 1: "🥇", 2: "🥈", 3: "🥉" };
const norm = (s) => (s || "").toLowerCase();

// Public, no-login community leaderboard — ranks the strongest published game of each type across all
// creators. Tabs filter by 3D / 2D / this week's climbers; a search box jumps to a creator or game.
// Signed-in creators see their own entries highlighted and can share a "Top of {genre}" rank card.
export default function LeaderboardPage() {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok
  const [copied, setCopied] = useState(false);
  const [tab, setTab] = useState("all"); // all | 3d | 2d | climbers
  const [rankCopiedId, setRankCopiedId] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/compiler/leaderboard")
      .then(({ data }) => { setData(data); setState("ok"); })
      .catch(() => setState("ok"));
  }, []);

  const allGenres = data?.genres || [];
  const climbers = data?.climbers || [];
  const match = (e) => !q.trim() || norm(e.title).includes(norm(q)) || norm(e.creator).includes(norm(q));

  const dimGenres = tab === "3d" ? allGenres.filter((g) => g.dim === "3d")
    : tab === "2d" ? allGenres.filter((g) => g.dim === "2d")
      : allGenres;
  const genres = q.trim()
    ? dimGenres.map((gr) => ({ ...gr, entries: gr.entries.filter(match), your_entry: gr.your_entry && match(gr.your_entry) ? gr.your_entry : null })).filter((gr) => gr.entries.length)
    : dimGenres;
  const shownClimbers = q.trim() ? climbers.filter(match) : climbers;
  const noSearchHits = q.trim() && (tab === "climbers" ? shownClimbers.length === 0 : genres.length === 0);

  const shareUrl = `${window.location.origin}/leaderboard`;
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* noop */ }
  };
  const nativeShare = async () => {
    try { await navigator.share({ title: "GameView Genre Leaderboard", text: "See the strongest AI-built games by genre on GameView Studio.", url: shareUrl }); } catch { /* cancelled */ }
  };

  const shareRank = async (e, label) => {
    const ogUrl = `${window.location.origin}/api/og/rank/${e.game_id}`;
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({ title: `#${e.rank} in ${label}`, text: `"${e.title}" ranks #${e.rank} in ${label} on GameView Studio!`, url: ogUrl });
      } else {
        await navigator.clipboard.writeText(ogUrl);
        setRankCopiedId(e.game_id); setTimeout(() => setRankCopiedId(null), 1800);
      }
    } catch { /* cancelled */ }
    window.open(`${window.location.origin}/api/og/rank/${e.game_id}/image.png`, "_blank", "noopener");
  };

  const TABS = [
    { k: "all", label: "All" },
    { k: "3d", label: "3D" },
    { k: "2d", label: "2D" },
    { k: "climbers", label: "This week", icon: TrendingUp },
  ];

  const Row = ({ e, label, showGenre }) => (
    <div className={`flex items-center gap-3 px-5 py-2.5 ${e.is_me ? "bg-emerald-500/10" : ""}`} data-testid={`leaderboard-entry-${e.game_id}`}>
      <div className="w-7 text-center text-sm font-bold text-white/50 shrink-0">{MEDAL[e.rank] || e.rank}</div>
      <div className="text-xl shrink-0">{e.cover_emoji}</div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold truncate flex items-center gap-1.5">
          {e.title}
          {e.is_new && <span className="text-[9px] font-extrabold px-1.5 py-0.5 rounded bg-[#38bdf8]/20 text-[#7dd3fc] shrink-0">NEW</span>}
          {e.is_me && <span className="text-[9px] font-extrabold px-1.5 py-0.5 rounded bg-emerald-500 text-black shrink-0" data-testid="leaderboard-you-badge">YOU</span>}
          {e.rank === 1 && !showGenre && <Crown className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
        </div>
        <div className="text-[11px] text-white/45 truncate">
          {showGenre ? <span className="text-white/60">{label} · </span> : null}by {e.creator}{e.plays ? ` · ${e.plays} plays` : ""}
        </div>
      </div>
      {e.is_me && (
        <button onClick={() => shareRank(e, label)} className={`hidden sm:inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold shrink-0 transition-colors ${e.rank === 1 ? "bg-amber-400/20 text-amber-300 hover:bg-amber-400/30" : "border border-white/15 text-white/70 hover:border-white/40"}`} data-testid={`leaderboard-rank-share-${e.game_id}`} title="Share your rank card">
          <Share2 className="w-3 h-3" /> {rankCopiedId === e.game_id ? "Copied!" : e.rank === 1 ? "Share #1" : "Share rank"}
        </button>
      )}
      <div className="hidden md:flex items-center w-24 shrink-0">
        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden w-full">
          <div className="h-full rounded-full" style={{ width: `${e.score}%`, background: scoreColor(e.score) }} />
        </div>
      </div>
      <div className="text-sm font-extrabold tabular-nums w-9 text-right shrink-0" style={{ color: scoreColor(e.score) }}>{e.score}</div>
      <Link to={`/g/${e.game_id}`} className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-bold bg-gradient-to-r from-[#0ea5e9] to-[#10b981] hover:opacity-95 transition-opacity shrink-0" data-testid={`leaderboard-play-${e.game_id}`}>
        <Play className="w-3 h-3" /> Play
      </Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#05070f] text-white" style={{ backgroundImage: "radial-gradient(1200px 600px at 50% -10%, rgba(251,191,36,0.16), transparent 60%)" }} data-testid="leaderboard-page">
      <div className="max-w-4xl mx-auto px-5 py-10">
        <div className="flex items-center justify-between gap-3 mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-white/80 hover:text-white transition-colors" data-testid="leaderboard-home">
            <Sparkles className="w-4 h-4 text-[#38bdf8]" /> GameView Studio
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={copyLink} className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold border border-white/20 hover:border-white/40 transition-colors" data-testid="leaderboard-copy">
              <LinkIcon className="w-3.5 h-3.5" /> {copied ? "Copied!" : "Share"}
            </button>
            {typeof navigator !== "undefined" && navigator.share && (
              <button onClick={nativeShare} className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold border border-white/20 hover:border-white/40 transition-colors" data-testid="leaderboard-native-share">
                <Share2 className="w-3.5 h-3.5" /> Share…
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-amber-300/80 mb-2">
          <Trophy className="w-4 h-4 text-amber-400" /> Community Leaderboard
        </div>
        <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight" data-testid="leaderboard-title">Genre Leaderboard</h1>
        <p className="text-white/55 mt-2 text-base max-w-xl">
          The strongest AI-built games of each type across GameView Studio — ranked by release readiness. See where your best games stand against the community.
        </p>

        {state === "loading" && <div className="py-28 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-white/50" /></div>}

        {state === "ok" && allGenres.length === 0 && (
          <div className="py-24 text-center" data-testid="leaderboard-empty">
            <div className="text-5xl mb-3">🏆</div>
            <h2 className="text-2xl font-extrabold mb-2">No games on the board yet</h2>
            <p className="text-white/60 mb-6">Publish a game and it'll compete here. Be the first to top a genre!</p>
            <Link to="/" className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-bold bg-gradient-to-r from-[#0ea5e9] to-[#7c3aed]">Make your own game</Link>
          </div>
        )}

        {state === "ok" && allGenres.length > 0 && (
          <>
            <p className="text-white/45 text-xs mt-4">
              <b className="text-white/70">{data.total_games}</b> published games across <b className="text-white/70">{data.types}</b> game types.
              {data.signed_in ? " Your games are highlighted." : " Sign in to see your rank."}
            </p>

            {/* Search */}
            <div className="relative mt-5" data-testid="leaderboard-search-wrap">
              <Search className="w-4 h-4 text-white/35 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a creator or game…" data-testid="leaderboard-search"
                className="w-full rounded-full bg-white/[0.04] border border-white/10 focus:border-[#38bdf8]/50 outline-none pl-10 pr-10 py-2.5 text-sm text-white placeholder:text-white/35" />
              {q && (
                <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white" data-testid="leaderboard-search-clear"><X className="w-4 h-4" /></button>
              )}
            </div>

            {/* Filter tabs */}
            <div className="flex items-center gap-1.5 mt-4 mb-6 flex-wrap" data-testid="leaderboard-tabs">
              {TABS.map((t) => {
                const Icon = t.icon;
                const active = tab === t.k;
                return (
                  <button key={t.k} onClick={() => setTab(t.k)} data-testid={`leaderboard-tab-${t.k}`}
                    className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${active ? "bg-white text-black" : "border border-white/15 text-white/70 hover:border-white/40"}`}>
                    {Icon && <Icon className="w-3.5 h-3.5" />} {t.label}
                    {t.k === "climbers" && climbers.length > 0 && <span className={`text-[10px] px-1.5 rounded-full ${active ? "bg-black/10" : "bg-white/10"}`}>{climbers.length}</span>}
                  </button>
                );
              })}
            </div>

            {noSearchHits && (
              <div className="py-16 text-center text-white/55" data-testid="leaderboard-search-empty">
                <Search className="w-8 h-8 mx-auto mb-3 text-white/30" />
                <p className="font-semibold">No games or creators match "{q}".</p>
                <p className="text-sm text-white/40 mt-1">Try a different name, or clear the search.</p>
              </div>
            )}

            {/* This week's climbers */}
            {tab === "climbers" && !noSearchHits && (
              shownClimbers.length === 0 ? (
                <div className="py-16 text-center text-white/55" data-testid="leaderboard-climbers-empty">
                  <TrendingUp className="w-8 h-8 mx-auto mb-3 text-white/30" />
                  <p className="font-semibold">No new games shipped this week — yet.</p>
                  <p className="text-sm text-white/40 mt-1">Publish a game and it'll show up here as a climber.</p>
                </div>
              ) : (
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden" data-testid="leaderboard-climbers">
                  <div className="flex items-center gap-2 px-5 py-3.5 border-b border-white/10 bg-white/[0.02]">
                    <TrendingUp className="w-4 h-4 text-[#38bdf8]" />
                    <h3 className="text-lg font-extrabold flex-1">This week's climbers</h3>
                    <span className="text-[11px] text-white/45">shipped in the last 7 days</span>
                  </div>
                  <div className="divide-y divide-white/[0.06]">
                    {shownClimbers.map((e) => <Row key={e.game_id} e={{ ...e, rank: e.climb_rank }} label={e.label} showGenre />)}
                  </div>
                </div>
              )
            )}

            {/* Genre boards */}
            {tab !== "climbers" && !noSearchHits && (
              <div className="space-y-6" data-testid="leaderboard-list">
                {genres.length === 0 ? (
                  <div className="py-16 text-center text-white/55" data-testid="leaderboard-dim-empty">No {tab.toUpperCase()} games on the board yet.</div>
                ) : genres.map((gr) => (
                  <div key={gr.template} className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden" data-testid={`leaderboard-genre-${gr.template}`}>
                    <div className="flex items-center gap-3 px-5 py-3.5 border-b border-white/10 bg-white/[0.02]">
                      <h3 className="text-lg font-extrabold flex-1 truncate flex items-center gap-2">{gr.label}
                        <span className="text-[9px] font-extrabold px-1.5 py-0.5 rounded bg-white/10 text-white/50 uppercase">{gr.dim}</span>
                      </h3>
                      <span className="text-[11px] text-white/45">{gr.count} game{gr.count === 1 ? "" : "s"}</span>
                      <span className="text-[11px] text-white/45">avg {gr.avg_score}</span>
                      <span className="text-sm font-extrabold tabular-nums" style={{ color: scoreColor(gr.top_score) }}>top {gr.top_score}</span>
                    </div>
                    <div className="divide-y divide-white/[0.06]">
                      {gr.entries.map((e) => <Row key={e.game_id} e={e} label={gr.label} />)}
                      {gr.your_entry && (
                        <div className="flex items-center gap-3 px-5 py-2.5 bg-emerald-500/10 border-t border-emerald-400/20" data-testid={`leaderboard-yourrank-${gr.template}`}>
                          <div className="w-7 text-center text-sm font-bold text-emerald-300 shrink-0">{gr.your_entry.rank}</div>
                          <div className="text-xl shrink-0">{gr.your_entry.cover_emoji}</div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold truncate flex items-center gap-1.5">{gr.your_entry.title}
                              <span className="text-[9px] font-extrabold px-1.5 py-0.5 rounded bg-emerald-500 text-black shrink-0">YOU</span>
                            </div>
                            <div className="text-[11px] text-white/45 truncate">your best in this genre</div>
                          </div>
                          <div className="text-sm font-extrabold tabular-nums w-9 text-right shrink-0" style={{ color: scoreColor(gr.your_entry.score) }}>{gr.your_entry.score}</div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-9 text-center">
              <Link to="/" className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-bold bg-gradient-to-r from-[#0ea5e9] to-[#7c3aed] hover:opacity-95 transition-opacity" data-testid="leaderboard-cta">
                <Sparkles className="w-4 h-4" /> Build a game that tops the board
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
